import type {MouseEvent, ReactElement, ReactNode} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import type {Poster, PosterOpts} from '@PosterWhiteboard/poster/poster.class';
import {GlobalPosterEditorJqueryElement} from '@Components/poster-editor/poster-editor.types';
import {PageOptions} from '@Components/poster-editor/components/page-options';
import {AddNewPageButton} from '@Components/poster-editor/components/add-new-page-button';
import {PosterPopupMenu} from '@Components/poster-editor/components/poster-popup-menu';
import {getChildElementWithMaxVisibleHeight} from '@Utils/dom.util';
import {TextItemPopup} from '@Components/poster-editor/components/text-item-popup';
import './poster-whiteboard.scss';
import {createPoster, loadPoster} from '@PosterWhiteboard/poster/poster-init';
import {PosterModeType} from '@PosterWhiteboard/poster/poster-mode.class';
import type {PosterTypeBackendObject} from '@PosterWhiteboard/poster/poster-backend.types';
import {ClickableDiv} from '@Components/clickable-div';
import {
  updateCanvasDimensions,
  updatePosterWhiteboardContainerDimensions,
  updatePosterWhiteboardDimensions,
  updateSpellCheckSettingsState,
} from '@Components/poster-editor/poster-editor-reducer';
import DragDropOverlay from '@Components/poster-editor/components/drag-drop-overlay';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {PosterWhiteboardLoader} from '@Components/poster-whiteboard/components/poster-whiteboard-loader';
import {watchIsAudioPlayable} from '@Utils/audio.util';
import {initFontsAndCategoriesAndScript} from '@Libraries/font-library';
import {checkClipboardReadPermissions} from '@Libraries/clipboard-library';
import {ImageItemPopup} from '@Components/poster-editor/components/image-item-popup';
import {PosterVerticalScroll, scrollPosterVerticalWithDelta} from '@Components/poster-whiteboard/components/poster-vertical-scroll';
import {PosterHorizontalScroll, scrollPosterHorizontalWithDelta} from '@Components/poster-whiteboard/components/poster-horizontal-scroll';
import {isEditorMobileVariant} from '@Components/poster-editor/library/poster-editor-library';
import {debounce} from '@Utils/misc.util';
import {config} from '@postermywall/fabricjs-2';
import {addElementToNotifyTest, TestNotificationId} from '@Libraries/test.library';
import styles from './poster-whiteboard.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import PosterChangingOverlay from '@Components/poster-whiteboard/components/poster-changing-overlay';
import VersionHistoryOverlay from '@Components/poster-whiteboard/components/version-history-overlay';
import {getFitToScreenScale, modeHasBufferForFitToScreen} from '@PosterWhiteboard/poster/poster-scaling.class';
import {CustomItemControls} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls';
import {EDITOR_BOTTOM_BAR_BOTTOM_OFFSET, EDITOR_DEFAULT_SCROLL_GAP, handleScrollingOnWhiteboard} from '@Components/poster-editor/library/poster-editor-scrolling-library';
import {FloatyEditorRightMargin} from '@Components/poster-whiteboard/components/floaty-editor-right-margin/floaty-editor-right-margin';
import {FloatyEditorLeftMargin} from '@Components/poster-whiteboard/components/floaty-editor-left-margin/floaty-editor-left-margin';
import {useIsPosterScrollableVertically} from '@Hooks/poster-editor/useIsPosterScrollableVertically';
import {POSTER_ELEMENT_CONTAINER} from '@Components/poster-whiteboard/poster-whiteboard.types';
import {useIsVideoOrHasMultiPages} from '@Hooks/poster-editor/useIsVideoOrHasMultiPages';
import {PageSlider} from '@Components/poster-editor/components/page-slider/page-slider';

// TODO: Move this to a better place
window.isPosterMaker = true;
window.posterEditor = {
  whiteboard: undefined,
  config: undefined,
  elements: {},
};

interface PosterWhiteboardProps {
  className?: string;
  isCenterAligned?: boolean;
  isMobileDisplay?: boolean;
  whiteboardClassName?: string;
  postWhiteboardElement?: ReactNode;
  postWhiteboardContainerElement?: ReactNode;
  posterOpts: PosterOpts;

  onInit?(): void;

  onError?(): void;
}

export type InitPosterOpts = LoadPosterOpts | CreatePosterOpts;

export interface LoadPosterOpts {
  posterHashId: string;
}

export interface CreatePosterOpts {
  posterType: PosterTypeBackendObject;
}

export function PosterWhiteboard({
  className = '',
  isCenterAligned = true,
  isMobileDisplay = false,
  whiteboardClassName = '',
  onInit = (): void => {},
  onError = (): void => {},
  ...props
}: PosterWhiteboardProps): ReactElement<any> {
  const posterEl = useRef<HTMLDivElement>(null);
  const postPosterElement = useRef<HTMLDivElement>(null);
  const whiteboardContainerRef = useRef<HTMLDivElement>(null);
  const whiteboardRef = useRef<HTMLDivElement>(null);
  const [isPosterLoading, setIsPosterLoading] = useState(false);
  const [scale, setScale] = useState<number | undefined>(undefined);
  const isPickingModeEnabled = useAppSelector((state) => {
    return state.posterEditor.isPickingModeEnabled;
  });
  const isRemoveBgModeEnabled = useAppSelector((state) => {
    return state.posterEditor.isRemoveBGModeEnabled;
  });
  const areStylesCopied = useAppSelector((state) => {
    return state.posterEditor.copiedStyles;
  });
  const isInGeneration = useAppSelector((state) => {
    return state.posterEditor.posterObject?.mode.details.type === PosterModeType.GENERATE;
  });
  const showFakeScrollBars = !isInGeneration && isEditorMobileVariant();
  const isDrawModeEnabled = useAppSelector((state) => {
    return state.posterEditor.posterObject?.drawing.isDrawModeOn;
  });
  const dispatch = useAppDispatch();
  const isPosterOverlayLoaderVisible = useAppSelector((state) => {
    return state.posterEditor.isPosterOverlayLoaderActive;
  });
  const isVersionHistoryOverlayVisible = useAppSelector((state) => {
    return state.posterEditor.isVersionHistoryOverlayActive;
  });
  const isPosterVideo = useIsVideoOrHasMultiPages();
  const isTimelineModeActive = useAppSelector((state) => {
    return state.posterEditor.isTimelineModeActive;
  });
  const bottomBarHeight = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.bottomBarHeight;
  });
  const posterRef = useRef<Poster | undefined>(undefined);
  const isPosterVerticallyScrollable = useIsPosterScrollableVertically();
  const isVersionHistoryModeActive = useAppSelector((state) => {
    return state.posterEditor.isVersionHistoryModeActive;
  });

  useEffect(() => {
    watchIsAudioPlayable();
    bindMediaButton();

    let mountedRef = true;
    let resizeObserverWhiteboard: ResizeObserver | undefined;
    let resizeObserverWhiteboardContainer: ResizeObserver | undefined;
    let resizeObserverCanvas: ResizeObserver | undefined;
    if (whiteboardContainerRef.current && whiteboardRef.current && posterEl.current && postPosterElement.current && window.posterEditor) {
      window.posterEditor.elements[GlobalPosterEditorJqueryElement.WHITEBOARD_CONTAINER] = $(whiteboardContainerRef.current);
      window.posterEditor.elements[GlobalPosterEditorJqueryElement.POST_POSTER_ELEMENT] = $(postPosterElement.current);
      window.posterEditor.elements[GlobalPosterEditorJqueryElement.WHITEBOARD] = $(whiteboardRef.current);

      resizeObserverWhiteboard = new ResizeObserver(debounce(onWhiteboardResize, 100));
      resizeObserverWhiteboardContainer = new ResizeObserver(debounce(onWhiteboardContainerResize, 100));
      resizeObserverCanvas = new ResizeObserver(debounce(onCanvasResize, 100));

      resizeObserverWhiteboard.observe(whiteboardRef.current);
      resizeObserverWhiteboardContainer.observe(whiteboardContainerRef.current);
      resizeObserverCanvas.observe(posterEl.current);
    }

    const handleScrollingFromScrollbars = (): void => {
      if (!window.posterEditor?.whiteboard?.hasFakeScrollbars()) {
        handleScrollingOnWhiteboard(0, 0);
      }
    };

    whiteboardContainerRef.current?.addEventListener('wheel', onPosterScroll, {passive: false});
    whiteboardContainerRef.current?.addEventListener('scroll', handleScrollingFromScrollbars, {passive: false});

    initPoster()
      .then((p) => {
        posterRef.current = p;
        if (mountedRef) {
          setGlobalVariables();
        } else {
          p.dispose();
        }
        onInit();
      })
      .catch((e) => {
        // TODO: Show error in UI
        console.error(e);
        onError();
      })
      .finally(() => {
        addElementToNotifyTest(TestNotificationId.POSTER_INITIALIZED);
      });

    return (): void => {
      unbindMediaButton();
      resizeObserverWhiteboard?.disconnect();
      resizeObserverWhiteboardContainer?.disconnect();
      resizeObserverCanvas?.disconnect();
      mountedRef = false;
      whiteboardContainerRef.current?.removeEventListener('wheel', onPosterScroll);
      whiteboardContainerRef.current?.removeEventListener('scroll', handleScrollingFromScrollbars);
      posterRef.current?.dispose();
    };
  }, []);

  const onWhiteboardResize = (): void => {
    if (whiteboardRef.current) {
      const rect = whiteboardRef.current.getBoundingClientRect();
      dispatch(
        updatePosterWhiteboardDimensions({
          width: rect.width,
          height: rect.height,
        })
      );
    }
  };

  const onWhiteboardContainerResize = (): void => {
    if (whiteboardContainerRef.current) {
      const rect = whiteboardContainerRef.current.getBoundingClientRect();
      dispatch(
        updatePosterWhiteboardContainerDimensions({
          width: rect.width,
          height: rect.height,
        })
      );
    }
  };

  const onCanvasResize = (): void => {
    if (posterEl.current) {
      const rect = posterEl.current.getBoundingClientRect();
      dispatch(
        updateCanvasDimensions({
          width: rect.width,
          height: rect.height,
        })
      );
    }
  };

  const onPosterScroll = (e: WheelEvent): void => {
    if (window.visualViewport?.scale !== 1) {
      return;
    }

    if (window.posterEditor?.whiteboard?.hasFakeScrollbars()) {
      e.stopPropagation();
      e.preventDefault();
      scrollPosterVerticalWithDelta(e.deltaY);
      scrollPosterHorizontalWithDelta(e.deltaX);
    } else {
      e.stopPropagation();
      e.preventDefault();
      if (!e.ctrlKey) {
        handleScrollingOnWhiteboard(e.deltaX, e.deltaY);
      }
    }
    window.posterEditor?.whiteboard?.scaling.zoomOnWheelEvent(e);
  };

  const initPoster = async (): Promise<Poster> => {
    await initFontsAndCategoriesAndScript();
    await checkClipboardReadPermissions();
    if (isEditorMobileVariant()) {
      config.disableGroupSelector = true;
    }
    let poster;
    if (props.posterOpts.mode.details.type === PosterModeType.CREATE) {
      poster = await createNewPoster();
    } else {
      poster = await loadExistingPoster();
    }
    poster.updateTitle();
    if (!poster.mode.isGeneration()) {
      void poster.play();
    }
    return poster;
  };

  const createNewPoster = async (): Promise<Poster> => {
    if (!posterEl.current) {
      throw new Error('No html element to init poster with');
    }

    setIsPosterLoading(true);
    setScale(() => {
      return getFitToScreenScale(
        props.posterOpts.mode.details.posterWidth,
        props.posterOpts.mode.details.posterHeight,
        modeHasBufferForFitToScreen(props.posterOpts.mode.details.type)
      );
    });
    const poster = await createPoster(posterEl.current, props.posterOpts);
    setIsPosterLoading(false);
    setGlobalVariables();
    trackPosterBuilderGA4Events(GA4EventName.CREATE_POSTER);
    return poster;
  };

  const loadExistingPoster = async (): Promise<Poster> => {
    if (!posterEl.current) {
      throw new Error('No html element to init poster with');
    }

    setIsPosterLoading(true);
    setScale(() => {
      return getFitToScreenScale(
        props.posterOpts.mode.details.posterWidth,
        props.posterOpts.mode.details.posterHeight,
        modeHasBufferForFitToScreen(props.posterOpts.mode.details.type)
      );
    });

    const poster = await loadPoster(
      posterEl.current,
      {
        onBackendObjectLaoded: () => {
          setIsPosterLoading(false);
        },
      },
      props.posterOpts
    );
    setGlobalVariables();
    return poster;
  };

  const setGlobalVariables = (): void => {
    if (whiteboardContainerRef.current && window.posterEditor) {
      let isScrolling: NodeJS.Timeout;
      const whiteboardContainerElement = whiteboardContainerRef.current;

      // CodeReviewDany: Dude this listener isn't suppose to be in setGlobalVariables
      whiteboardContainerElement.addEventListener('scroll', () => {
        clearTimeout(isScrolling);

        isScrolling = setTimeout(() => {
          const pageContainerElementHeight = $('.pmw-page-container').first().outerHeight(true) ?? 0;
          const canvasContainers = Array.from(document.getElementsByClassName('canvas-container')) as HTMLElement[];

          const startPageCheckFrom = pageContainerElementHeight > 0 ? Math.floor(whiteboardContainerElement.scrollTop / pageContainerElementHeight) : 0;
          const activePageElement = getChildElementWithMaxVisibleHeight(canvasContainers.slice(startPageCheckFrom), whiteboardContainerElement);

          const newActivePageId = activePageElement.closest('.pmw-page-container')?.getAttribute('data-id');

          if (newActivePageId) {
            window.posterEditor?.whiteboard?.setCurrentPage(newActivePageId);
          }
        }, 500);
      });
    }
  };

  const onWhiteboardContainerClick = (e: MouseEvent): void => {
    if (e.target === whiteboardRef.current || e.target === whiteboardContainerRef.current) {
      dispatch(updateSpellCheckSettingsState(false));
      window.posterEditor?.whiteboard?.clearSelection();
      exitDrawModeIfEnabled();
    }
  };

  const bindMediaButton = (): void => {
    if (window.navigator?.mediaSession) {
      navigator.mediaSession.setActionHandler('play', (): void => {
        void window.posterEditor?.whiteboard?.play();
      });
      navigator.mediaSession.setActionHandler('pause', (): void => {
        void window.posterEditor?.whiteboard?.pause();
      });
    }
  };

  const unbindMediaButton = (): void => {
    if (window.navigator?.mediaSession) {
      navigator.mediaSession.setActionHandler('play', null);
      navigator.mediaSession.setActionHandler('pause', null);
    }
  };

  const exitDrawModeIfEnabled = (): void => {
    if (isDrawModeEnabled) {
      window.posterEditor?.whiteboard?.drawing.updateFromObject({
        isDrawModeOn: false,
      });
    }
  };
  const getVerticalPaddingForWhiteboard = (): string => {
    if (isMobileDisplay || isInGeneration) return '';

    const isZoomedIn = isPosterVerticallyScrollable;
    const isVideo = isPosterVideo;

    if (isZoomedIn) {
      return isVideo && isTimelineModeActive ? styles.verticalPaddingForZoomedInPoster : styles.generalVerticalPaddingForZoomedInPoster;
    }

    return isVideo && isTimelineModeActive ? styles.verticalPaddingForZoomedInPoster : styles.generalVerticalPadding;
  };

  const getPaddingForWhiteboard = (): string | undefined => {
    if (isMobileDisplay || isInGeneration) return '';
    const zoomBuffer = isPosterVerticallyScrollable ? EDITOR_DEFAULT_SCROLL_GAP : 0;

    if (!isTimelineModeActive) {
      return undefined;
    }

    return isPosterVideo ? `${(bottomBarHeight ?? 0) + zoomBuffer + EDITOR_BOTTOM_BAR_BOTTOM_OFFSET}px` : undefined;
  };

  return (
    <ClickableDiv
      id="poster-whiteboard-container"
      style={{
        paddingBottom: getPaddingForWhiteboard(),
      }}
      className={`flex-h-row whiteboard-container ${isTimelineModeActive ? 'timeline-mode-active' : 'scroll-mode-active'} ${isMobileDisplay ? '' : styles.containerForDesktop} ${getVerticalPaddingForWhiteboard()} ${styles.container} ${className} ${
        showFakeScrollBars && !isInGeneration ? styles.hideScroll : styles.showScroll
      }`}
      enableFocusOutline={false}
      ref={whiteboardContainerRef}
      onMouseDown={onWhiteboardContainerClick}
      onClick={() => {}}
    >
      <DragDropOverlay />
      {isPosterLoading && !isPosterOverlayLoaderVisible ? <PosterWhiteboardLoader isCenterAligned={isCenterAligned} posterMode={props.posterOpts.mode} /> : null}
      {isPosterLoading ? null : <FloatyEditorLeftMargin />}
      {isPosterOverlayLoaderVisible ? (
        <PosterChangingOverlay
          posterOpts={props.posterOpts}
          isPosterLoading={isPosterLoading}
          className={`${styles.posterOverlayLoader} ${styles.allMargin}`}
          width={scale ? scale * props.posterOpts.mode.details.posterWidth : undefined}
          height={scale ? scale * props.posterOpts.mode.details.posterHeight : undefined}
        />
      ) : null}
      <div
        ref={whiteboardRef}
        className={`${styles.whiteboard} ${showFakeScrollBars ? styles.hasFakeScroll : ''} ${isPosterLoading || isPosterOverlayLoaderVisible ? '_hidden' : ''} ${
          isCenterAligned ? `flex-items-center ${styles.centerAlignedWhiteboard}` : ''
        } flex-v-row ${whiteboardClassName}`}
      >
        {/*Move the isPickingModeEnabled || areStylesCopied check to a function called doShowCursorOverlay, ands rename containerWhilePickingModeEnabled to containerWithCursorOverlay*/}
        <div
          ref={posterEl}
          className={`${POSTER_ELEMENT_CONTAINER} flex-v-row ${styles.posterElement} ${isPickingModeEnabled || areStylesCopied || isRemoveBgModeEnabled ? styles.containerWhilePickingModeEnabled : ''} ${isVersionHistoryModeActive ? styles.canvasDisabledInVersionHistory : ''} ${isPosterOverlayLoaderVisible ? '_hidden' : ''}`}
        >
          {isVersionHistoryOverlayVisible ? <VersionHistoryOverlay className={styles.versionHistoryOverlay} /> : null}
          <CustomItemControls />
          <TextItemPopup />
          <ImageItemPopup />
        </div>
        <div ref={postPosterElement} className={styles.postPosterElement}>
          <AddNewPageButton />
          {props.postWhiteboardElement ?? null}
        </div>
      </div>
      {isPosterLoading ? null : <FloatyEditorRightMargin />}
      <PageOptions />
      <PosterPopupMenu />
      <div id="buffer" className={styles.buffer}>
        <canvas id="bufferCanvas" width="1" height="1" />
      </div>
      {props.postWhiteboardContainerElement ?? null}
      {isEditorMobileVariant() ? <PosterVerticalScroll /> : null}
      {isEditorMobileVariant() ? <PosterHorizontalScroll /> : null}
    </ClickableDiv>
  );
}
